import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { getMap, getRun, improveMap, reanalyzeMap, runWebsocketUrl } from "./api";
import { Header } from "./components/Header";
import { LandingPage } from "./components/LandingPage";
import { HelpOverlay } from "./components/Overlays";
import { ProcessingView } from "./components/ProcessingView";
import { ResultView } from "./components/ResultView";
import { ReviewView } from "./components/ReviewView";
import type { LiveEvent, MapRecord, ProcessingRun } from "./types";

export default function App() {
  const [helpOpen, setHelpOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  function goHome() { navigate("/"); window.scrollTo({ top: 0, behavior: "smooth" }); }
  function goEvents() {
    if (location.pathname !== "/") { navigate("/#termine"); return; }
    document.getElementById("termine")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="site-shell">
      <div className="app-frame">
        <Header onHome={goHome} onEvents={goEvents} onHelp={() => setHelpOpen(true)} />
        <Routes>
          <Route path="/" element={<LandingPage onOpenMap={(token) => navigate(`/karte/${token}`)} />} />
          <Route path="/karte/:token" element={<MapPage onHome={goHome} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <footer><span>Hofflohmarkt Map</span><span>Unabhängiges Projekt · Offene Geodaten · Läuft auf deinem Server</span></footer>
      {helpOpen && <HelpOverlay onClose={() => setHelpOpen(false)} />}
    </div>
  );
}

function MapPage({ onHome }: { onHome: () => void }) {
  const { token = "" } = useParams();
  const [map, setMap] = useState<MapRecord | null>(null);
  const [logs, setLogs] = useState<LiveEvent[]>([]);
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const socketRef = useRef<WebSocket | null>(null);

  const loadMap = useCallback(async () => {
    try { setMap(await getMap(token)); setError(""); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Karte konnte nicht geladen werden"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { setLoading(true); setLogs([]); void loadMap(); }, [loadMap]);

  const refreshRun = useCallback(async (runId: string) => {
    try {
      const run = await getRun(runId);
      if (run.status === "completed") { await loadMap(); return; }
      setMap((current) => current ? { ...current, run } : current);
    } catch { /* polling will retry */ }
  }, [loadMap]);

  const startImprovement = useCallback(async () => {
    const run = await improveMap(token);
    setLogs([]);
    setMap((current) => current ? { ...current, run } : current);
  }, [token]);

  const reanalyze = useCallback(async () => {
    const updated = await reanalyzeMap(token);
    setMap(updated);
  }, [token]);

  useEffect(() => {
    socketRef.current?.close();
    setConnected(false);
    const run = map?.run;
    if (!run || run.status === "completed" || run.status === "failed" || run.status === "needs_review") return;
    const socket = new WebSocket(runWebsocketUrl(run.id));
    socketRef.current = socket;
    socket.onopen = () => setConnected(true);
    socket.onclose = () => setConnected(false);
    socket.onerror = () => setConnected(false);
    socket.onmessage = (message) => {
      const event = JSON.parse(message.data) as LiveEvent;
      if (event.type === "heartbeat" || event.type === "run.snapshot") return;
      if (["run.log", "run.needs_input", "run.needs_review", "run.failed"].includes(event.type)) setLogs((current) => [...current.slice(-29), event]);
      if (event.type !== "run.log") void refreshRun(run.id);
    };
    const poll = window.setInterval(() => void refreshRun(run.id), 2500);
    return () => { window.clearInterval(poll); socket.close(); };
  }, [map?.run?.id, map?.run?.status, refreshRun]);

  if (loading) return <main className="map-loading"><span className="loading-spinner" /><strong>Karte wird geladen …</strong></main>;
  if (error || !map) return <main className="fatal-card"><h1>Diese Karte ist nicht verfügbar.</h1><p>{error || "Der Link ist ungültig oder abgelaufen."}</p><button className="button button-primary" onClick={onHome}>Zur Terminübersicht</button></main>;
  if (!map.run) return <main className="fatal-card"><h1>Keine Verarbeitung gefunden.</h1><p>Für diese Karte existiert noch kein Verarbeitungslauf.</p><button className="button button-primary" onClick={onHome}>Zur Terminübersicht</button></main>;
  if (map.run.status === "completed" && map.run.result?.geojson) return <ResultView map={map} onHome={onHome} onImprove={startImprovement} onReanalyze={reanalyze} />;
  if (map.run.status === "needs_review" && map.run.result?.review) return <ReviewView map={map} run={map.run} onRun={(run) => setMap((current) => current ? { ...current, run } : current)} onHome={onHome} />;
  if (map.run.status === "failed") return <main className="fatal-card"><h1>Die Karte konnte nicht verarbeitet werden.</h1><p>{map.run.error || map.run.message}</p><button className="button button-primary" onClick={onHome}>{map.kind === "event" ? "Zurück zum Termin" : "Andere Karte versuchen"}</button></main>;
  return <ProcessingView map={map} run={map.run} logs={logs} connected={connected} onRun={(run: ProcessingRun) => setMap((current) => current ? { ...current, run } : current)} />;
}
